import { ContextMenu2 } from "@blueprintjs/popover2";
import { Identifier } from "dnd-core";
import React, { useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";

import { useGetSinglePlay } from "../../../../redux/slices";
import { DragItem, ItemTypes } from "../../ItemTypes";
import { usePlaybookContext } from "../../playbook-context";
import RemovePlayDialog from "../dialogs/remove-play-dialog";
import PlayItemContextMenu from "../play-item-context-menu/play-item-context-menu";

interface DraggablePlayProps {
  playId: string;
  index: number;
  children: React.ReactNode;
}

const DraggablePlay = ({ playId, index, children }: DraggablePlayProps) => {
  const [showRemovePlayDialog, setShowRemovePlayDialog] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: play } = useGetSinglePlay(playId);

  const { movePlayLocal, saveLocalPlaybook } = usePlaybookContext();

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemTypes.PLAY,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      movePlayLocal(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
    drop() {
      saveLocalPlaybook();
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.PLAY,
    item: () => {
      return { playId, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));
  return (
    <ContextMenu2
      key={playId}
      style={{ cursor: "pointer" }}
      content={<PlayItemContextMenu onRemovePlay={() => setShowRemovePlayDialog(true)} />}
    >
      <div ref={ref} data-handler-id={handlerId}>
        {children}
        {play && (
          <RemovePlayDialog
            show={showRemovePlayDialog}
            handleClose={() => setShowRemovePlayDialog(false)}
            play={play}
          />
        )}
      </div>
    </ContextMenu2>
  );
};

export default DraggablePlay;
